import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Download, FileDown, RefreshCw, Link2, ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { downloadExport } from "@/lib/downloadExport";
import LeadsUploadDialog from "@/components/leads/LeadsUploadDialog";

/** §8.5 Lead Upload — dedicated page: template, upload, import history + error reports. */
export default function ImportHistoryPanel() {
  const { activeSlug } = useClient();
  const [imports, setImports] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/leads/imports")
      .then((r) => setImports(r.data.imports || []))
      .catch(() => toast.error("Failed to load import history"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load, activeSlug]);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/leads/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "kippu-leads-template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Template download failed");
    }
  };

  const downloadErrors = async (imp) => {
    try {
      const res = await api.get(`/leads/imports/${imp.id}/errors`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `import-errors-${imp.id.slice(0, 8)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No error report available for this import");
    }
  };

  const exportHistory = async (format) => {
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadExport("/leads/imports/export", { format },
        `kippu-import-history-${activeSlug}-${stamp}.${format}`);
      toast.success(`Import history downloaded (.${format})`);
    } catch {
      toast.error("Export failed");
    }
  };

  const exportUrls = async (format) => {
    setExporting(true);
    try {
      const base = window.location.origin;
      const res = await api.get(
        `/leads/export-urls?format=${format}&base=${encodeURIComponent(base)}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kippu-lead-urls-${activeSlug}-${new Date().toISOString().slice(0, 10)}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Lead URL export ready (.${format}) — every lead, mapped to its Customer ID`);
    } catch {
      toast.error("Lead URL export failed");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card data-testid="import-history-panel">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2">
          <Upload className="w-4 h-4 text-slate-500" /> Lead Upload &amp; Import History
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Template
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={exporting} data-testid="export-lead-urls-btn">
                <Link2 className={`w-3.5 h-3.5 mr-1.5 ${exporting ? "animate-pulse" : ""}`} />
                {exporting ? "Exporting…" : "Export"}
                <ChevronDown className="w-3 h-3 ml-1.5 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => exportUrls("xlsx")} data-testid="export-lead-urls-xlsx">
                <FileDown className="w-3.5 h-3.5 mr-2 text-emerald-600" /> Lead URLs — Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportUrls("csv")} data-testid="export-lead-urls-csv">
                <FileDown className="w-3.5 h-3.5 mr-2 text-sky-600" /> Lead URLs — CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportHistory("xlsx")} data-testid="export-history-xlsx">
                <FileDown className="w-3.5 h-3.5 mr-2 text-emerald-600" /> Import History — Excel
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportHistory("csv")} data-testid="export-history-csv">
                <FileDown className="w-3.5 h-3.5 mr-2 text-sky-600" /> Import History — CSV
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-imports-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button size="sm" onClick={() => setUploadOpen(true)}
                  className="bg-indigo-600 hover:bg-indigo-700" data-testid="settings-upload-leads-btn">
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload Leads
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {imports.length === 0 ? (
          <div className="text-xs text-slate-400 py-6 text-center">No imports yet — upload your first lead file.</div>
        ) : (
          <div className="border border-slate-200 rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="text-left px-3 py-1.5">Date</th>
                  <th className="text-left px-2 py-1.5">File</th>
                  <th className="text-left px-2 py-1.5">Uploaded by</th>
                  <th className="text-right px-2 py-1.5">Rows</th>
                  <th className="text-right px-2 py-1.5">Imported</th>
                  <th className="text-right px-2 py-1.5">Rejected</th>
                  <th className="px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {imports.map((imp) => {
                  const s = imp.summary || {};
                  return (
                    <tr key={imp.id} className="border-t border-slate-100" data-testid={`import-row-${imp.id}`}>
                      <td className="px-3 py-1.5 font-mono text-[10px] text-slate-500">
                        {(imp.created_at || "").slice(0, 16).replace("T", " ")}
                      </td>
                      <td className="px-2 py-1.5 text-slate-700">{imp.file_name}</td>
                      <td className="px-2 py-1.5 text-slate-500">{imp.uploaded_by_name || imp.uploaded_by_email}</td>
                      <td className="px-2 py-1.5 text-right">{s.total_rows ?? "—"}</td>
                      <td className="px-2 py-1.5 text-right">
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-[10px]">{s.valid ?? 0}</Badge>
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {s.invalid > 0
                          ? <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 text-[10px]">{s.invalid}</Badge>
                          : <span className="text-slate-400">0</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {s.invalid > 0 && (
                          <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]"
                                  onClick={() => downloadErrors(imp)} data-testid={`import-errors-${imp.id}`}>
                            <FileDown className="w-3 h-3 mr-1" /> Errors
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <LeadsUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onComplete={load} />
    </Card>
  );
}
